import { useState, useEffect, useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  getIntegrationsApi,
  resolveIntegrationsWorkspaceId,
} from '../../../lib/integrationsApi';
import {
  saveWorkspaceConnectorCredentials,
  listConnectorConfigs,
} from '../../../lib/connectorConfigsService';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import {
  loadMiroColorMaps,
  getColorRulesForEntity,
  type MiroColorRule,
} from '../../../lib/fieldMappingRegistry';
import { IntegrationStrip } from './integrations/IntegrationStrip';
import { MiroConfigModal } from './integrations/MiroConfigModal';
import { MiroImportModal } from './integrations/MiroImportModal';
import { MiroExportModal } from './integrations/MiroExportModal';
import { JiraConfigModal } from './integrations/JiraConfigModal';
import { JiraExportModal } from './integrations/JiraExportModal';
import { HierarchyPreviewModal } from './integrations/HierarchyPreviewModal';
import { useMiroConnection } from './integrations/useMiroConnection';
import { useJiraConnection } from './integrations/useJiraConnection';
import { useSpreadsheetSync } from './integrations/useSpreadsheetSync';
import type { IntegrationSyncPanelProps, SyncStatus } from './integrations/integrationTypes';

export type { PriorityExportRecord, ImportItemPayload } from './integrations/integrationTypes';

export function IntegrationSyncPanel({
  listId,
  listName,
  priorities,
  onImportItems,
  onNotification,
  onCsvImportClick,
  hideExports = false,
  onCsvTemplateClick,
  rankImport,
}: IntegrationSyncPanelProps) {
  // Must use the real workspace UUID — listId is a page sentinel ("intake",
  // "items-library") and connector_configs.workspace_id is a UUID FK. Passing
  // listId made POST /miro/connect fail persist with "Failed to connect to Miro".
  const workspaceIdFromStore = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceId = resolveIntegrationsWorkspaceId(
    workspaceIdFromStore,
    getCurrentWorkspaceId(),
  );
  const api = useMemo(() => getIntegrationsApi(workspaceId ?? 'default'), [workspaceId]);

  const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);

  // One-time migration banner: offer to move localStorage tokens to connector_configs
  const [showMigrationBanner, setShowMigrationBanner] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationDone, setMigrationDone] = useState(false);

  // Sync status
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    pendingMiroItems: 0,
    pendingJiraItems: 0,
    linkedItems: 0,
    brokenLinks: 0,
  });

  // Configured color → metadata rules for items (loaded from connector_configs)
  const [itemColorRules, setItemColorRules] = useState<MiroColorRule[]>([]);

  const miro = useMiroConnection({
    api,
    workspaceId,
    listId,
    listName,
    priorities,
    onImportItems,
    onNotification,
    itemColorRules,
    setSyncStatus,
  });
  const jira = useJiraConnection({
    api,
    workspaceId,
    listId,
    listName,
    priorities,
    onNotification,
    setSyncStatus,
  });
  const spreadsheet = useSpreadsheetSync({ listName, priorities, onImportItems, onNotification, rankImport });

  const { miroConnected, setMiroConnected, setMiroBoards, setMiroTemplates, setSelectedMiroTemplate, setMiroAccessToken, setMiroTeamId } = miro;
  const { jiraConnected, setJiraConnected, setJiraProjects, setJiraSiteUrl, setJiraEmail, setJiraApiToken } = jira;

  // Check API availability and auto-reconnect on mount; also load color mappings
  useEffect(() => {
    if (workspaceId) {
      loadMiroColorMaps(workspaceId).then((maps) => {
        setItemColorRules(getColorRulesForEntity(maps, 'items'));
      }).catch(() => {/* non-fatal */});
    }

    const checkApiAndReconnect = async () => {
      try {
        await api.checkHealth();
        setApiAvailable(true);
        if (!workspaceId) return;

        // Prefer GET /boards (already-persisted creds). POST /connect re-persists
        // and was the source of the console 500s on every Submit Idea load.
        const storedMiroToken = localStorage.getItem('peak_miro_token');
        if (storedMiroToken && !miroConnected) {
          const storedTeamId = localStorage.getItem('peak_miro_team_id');
          try {
            try {
              const [boards, templates] = await Promise.all([
                api.listMiroBoards(),
                api.listMiroTemplates(),
              ]);
              setMiroConnected(true);
              setMiroBoards(boards);
              setMiroTemplates(templates);
              if (templates.length > 0) {
                setSelectedMiroTemplate(templates[0].id);
              }
            } catch {
              const result = await api.connectMiro(storedMiroToken, storedTeamId || undefined);
              if (result.connected) {
                setMiroConnected(true);
                const [boards, templates] = await Promise.all([
                  api.listMiroBoards(),
                  api.listMiroTemplates(),
                ]);
                setMiroBoards(boards);
                setMiroTemplates(templates);
                if (templates.length > 0) {
                  setSelectedMiroTemplate(templates[0].id);
                }
              } else {
                console.warn('Auto-reconnect to Miro failed:', result.error);
              }
            }
          } catch (e) {
            console.warn('Auto-reconnect to Miro failed:', e);
          }
        }

        const storedJiraToken = localStorage.getItem('peak_jira_token');
        const storedJiraSite = localStorage.getItem('peak_jira_site_url');
        const storedJiraEmail = localStorage.getItem('peak_jira_email');
        if (storedJiraToken && storedJiraSite && storedJiraEmail && !jiraConnected) {
          try {
            try {
              const projects = await api.listJiraProjects();
              setJiraConnected(true);
              setJiraProjects(projects);
            } catch {
              const result = await api.connectJira(storedJiraSite, storedJiraEmail, storedJiraToken);
              if (result.connected) {
                setJiraConnected(true);
                const projects = await api.listJiraProjects();
                setJiraProjects(projects);
              } else {
                console.warn('Auto-reconnect to Jira failed:', result.error);
              }
            }
          } catch (e) {
            console.warn('Auto-reconnect to Jira failed:', e);
          }
        }
      } catch {
        setApiAvailable(false);
      }
    };
    checkApiAndReconnect();
    // Re-run when the workspace UUID becomes available (tenant persist hydrates
    // after first paint). Do not depend on api/miroConnected/jiraConnected —
    // those change as this effect itself connects and would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Check whether localStorage has tokens that haven't been migrated to connector_configs yet
  useEffect(() => {
    const miroToken = (() => { try { return localStorage.getItem('peak_miro_token'); } catch { return null; } })();
    const jiraToken = (() => { try { return localStorage.getItem('peak_jira_token'); } catch { return null; } })();
    if (!miroToken && !jiraToken) return;

    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;

    // Only show the banner if connector_configs doesn't already have a workspace row for these types
    listConnectorConfigs(workspaceId).then(({ data }) => {
      const hasMiroDb = data.some((r) => r.connectorType === 'miro' && r.userId === null && r.isActive && Object.keys(r.authData).length > 0);
      const hasJiraDb = data.some((r) => r.connectorType === 'jira' && r.userId === null && r.isActive && Object.keys(r.authData).length > 0);
      const needsMigration = (miroToken && !hasMiroDb) || (jiraToken && !hasJiraDb);
      if (needsMigration) setShowMigrationBanner(true);
    });
  }, []);

  // Hydrate connector credentials from DB on mount (DB is source of truth; localStorage is only a warm cache)
  useEffect(() => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;

    listConnectorConfigs(workspaceId).then(({ data }) => {
      const miroRow = data.find((r) => r.connectorType === 'miro' && r.isActive && r.authData?.accessToken);
      if (miroRow?.authData?.accessToken) {
        const token = miroRow.authData.accessToken as string;
        const teamId = (miroRow.authData.teamId as string) || '';
        setMiroAccessToken(token);
        if (teamId) setMiroTeamId(teamId);
        // Keep localStorage in sync so the Miro API helper can read it
        try {
          localStorage.setItem('peak_miro_token', token);
          if (teamId) localStorage.setItem('peak_miro_team_id', teamId);
        } catch { /* ignore */ }
      }

      const jiraRow = data.find((r) => r.connectorType === 'jira' && r.isActive && r.authData?.apiToken);
      if (jiraRow?.authData?.apiToken) {
        const siteUrl = (jiraRow.authData.siteUrl as string) || '';
        const email = (jiraRow.authData.email as string) || '';
        const token = jiraRow.authData.apiToken as string;
        if (siteUrl) setJiraSiteUrl(siteUrl);
        if (email) setJiraEmail(email);
        setJiraApiToken(token);
        try {
          if (siteUrl) localStorage.setItem('peak_jira_site_url', siteUrl);
          if (email) localStorage.setItem('peak_jira_email', email);
          localStorage.setItem('peak_jira_token', token);
        } catch { /* ignore */ }
      }
    });
  }, [setMiroAccessToken, setMiroTeamId, setJiraSiteUrl, setJiraEmail, setJiraApiToken]); // setters are stable state setters

  return (
    <div className="space-y-4">
      {/* One-time migration banner — offer to save localStorage tokens to connector_configs */}
      {showMigrationBanner && !migrationDone && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-[var(--text-ter)] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-[var(--text-sec)]">Save your connector credentials to your account</h3>
              <p className="text-sm text-[var(--text-sec)] mt-0.5">
                Your Miro or Jira credentials are currently stored only in this browser. Save them to your workspace so they work across all devices and teammates.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={async () => {
                    setMigrating(true);
                    const workspaceId = getCurrentWorkspaceId();
                    if (!workspaceId) { setMigrating(false); return; }
                    try {
                      const miroToken = localStorage.getItem('peak_miro_token');
                      const miroTeamId = localStorage.getItem('peak_miro_team_id');
                      if (miroToken) {
                        await saveWorkspaceConnectorCredentials(workspaceId, 'miro', { accessToken: miroToken, teamId: miroTeamId ?? undefined });
                        localStorage.removeItem('peak_miro_token');
                        localStorage.removeItem('peak_miro_team_id');
                      }
                      const jiraToken = localStorage.getItem('peak_jira_token');
                      const jiraSite = localStorage.getItem('peak_jira_site_url');
                      const jiraEmail = localStorage.getItem('peak_jira_email');
                      if (jiraToken && jiraSite && jiraEmail) {
                        await saveWorkspaceConnectorCredentials(workspaceId, 'jira', { apiToken: jiraToken, siteUrl: jiraSite, email: jiraEmail });
                        localStorage.removeItem('peak_jira_token');
                        localStorage.removeItem('peak_jira_site_url');
                        localStorage.removeItem('peak_jira_email');
                      }
                      setMigrationDone(true);
                      setShowMigrationBanner(false);
                    } catch {
                      // silently fail — user can retry
                    } finally {
                      setMigrating(false);
                    }
                  }}
                  disabled={migrating}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-[var(--slate)] hover:bg-[var(--slate)] text-white font-medium transition-colors disabled:opacity-50"
                >
                  {migrating ? 'Saving…' : 'Save to account'}
                </button>
                <button
                  onClick={() => setShowMigrationBanner(false)}
                  className="px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--slate-dim)] transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API not available warning - Miro/Jira need the engine; Excel/CSV works without it */}
      {apiAvailable === false && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-[var(--mustard)] shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-[var(--mustard)]">Integrations API Unavailable</h3>
              <p className="text-sm text-[var(--mustard)]">
                Miro and Jira require the integrations engine. Start it with{' '}
                <code className="px-2 py-0.5 bg-[var(--mustard-dim)] rounded text-xs">npm run dev</code> in{' '}
                <code className="px-2 py-0.5 bg-[var(--mustard-dim)] rounded text-xs">engines/integrations-engine</code>.
                Excel/CSV import and export work without the engine.
              </p>
            </div>
          </div>
        </div>
      )}

      <IntegrationStrip
        miroConnected={miroConnected}
        jiraConnected={jiraConnected}
        hideExports={hideExports}
        syncStatus={syncStatus}
        csvExcelInputRef={spreadsheet.csvExcelInputRef}
        onCsvImportClick={onCsvImportClick}
        onCsvTemplateClick={onCsvTemplateClick}
        handleExcelCsvFileSelected={spreadsheet.handleExcelCsvFileSelected}
        handleDownloadImportTemplateCsv={spreadsheet.handleDownloadImportTemplateCsv}
        handleDownloadImportTemplateExcel={spreadsheet.handleDownloadImportTemplateExcel}
        handleExportCsv={spreadsheet.handleExportCsv}
        handleExportExcel={spreadsheet.handleExportExcel}
        setShowMiroConfig={miro.setShowMiroConfig}
        setShowImportMiro={miro.setShowImportMiro}
        setShowExportMiro={miro.setShowExportMiro}
        setShowJiraConfig={jira.setShowJiraConfig}
        setShowExportJira={jira.setShowExportJira}
        setSelectedPriorities={jira.setSelectedPriorities}
      />

      <MiroConfigModal miro={miro} />
      <JiraConfigModal jira={jira} />
      <MiroImportModal miro={miro} workspaceId={workspaceId} itemColorRules={itemColorRules} />
      <JiraExportModal jira={jira} priorities={priorities} />
      <HierarchyPreviewModal jira={jira} />
      <MiroExportModal miro={miro} workspaceId={workspaceId} priorities={priorities} listName={listName} />
    </div>
  );
}
